import React, { useContext, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  darken,
  makeStyles,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { getMentionsFromText, updateComment } from '../../api/comments';
import { processTextAndFilesForSave } from '../../api/files';
import { OperationInProgressContext } from '../../contexts/OperationInProgressContext/OperationInProgressContext';
import { CommentsContext } from '../../contexts/CommentsContext/CommentsContext';
import { ISSUE_TYPE, QUESTION_TYPE, REPORT_TYPE, SUGGEST_CHANGE_TYPE, TODO_TYPE } from '../../constants/comments';
import { InvestiblesContext } from '../../contexts/InvestibesContext/InvestiblesContext';
import { getGroupPresences, getMarketPresences } from '../../contexts/MarketPresencesContext/marketPresencesHelper';
import { MarketPresencesContext } from '../../contexts/MarketPresencesContext/MarketPresencesContext';
import _ from 'lodash';
import { onCommentOpen } from '../../utils/commentFunctions';
import { MarketStagesContext } from '../../contexts/MarketStagesContext/MarketStagesContext';
import { findMessageOfType } from '../../utils/messageUtils';
import { NotificationsContext } from '../../contexts/NotificationsContext/NotificationsContext';
import { Clear, Feedback, Note, Update } from '@material-ui/icons';
import SpinningIconLabelButton from '../Buttons/SpinningIconLabelButton';
import { useEditor } from '../TextEditors/quillHooks';
import { deleteOrDehilightMessages } from '../../api/users';
import { getQuillStoredState, resetEditor } from '../TextEditors/Utilities/CoreUtils';
import { removeMessages } from '../../contexts/NotificationsContext/notificationsContextReducer';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import BlockIcon from '@material-ui/icons/Block';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { allImagesLoaded } from '../../utils/windowUtils';
import { ERROR, sendInfoPersistent, sendIntlMessage } from '../../utils/userMessage';
import WizardStepContainer from '../AddNewWizards/WizardStepContainer';
import WizardStepButtons from '../AddNewWizards/WizardStepButtons';
import { WizardStylesContext } from '../AddNewWizards/WizardStylesContext';
import QuestionIcon from '@material-ui/icons/ContactSupport';
import GravatarGroup from '../Avatars/GravatarGroup';
import { GroupMembersContext } from '../../contexts/GroupMembersContext/GroupMembersContext';
import { calculateInvestibleVoters } from '../../utils/votingUtils';
import { MarketsContext } from '../../contexts/MarketsContext/MarketsContext';
import { DARK_TEXT_BACKGROUND_COLOR } from '../Buttons/ButtonConstants';
import { formCommentLink } from '../../utils/marketIdPathFunctions';
import { useHistory } from 'react-router';
import { navigate } from '../../utils/marketIdPathFunctions';
import useSubmissionNavigation from '../../utils/useSubmissionNavigation';

const useStyles = makeStyles((theme) => ({
  visible: {
    overflow: 'visible',
    backgroundColor: 'transparent'
  },
  cardContent: {
    padding: 0,
    paddingTop: '0.5rem'
  },
  // T-all-2209 (Q-all-155 O-1): the edit screen used to read as two detached
  // bordered blocks - the editor box and the floating toolbar pill - with a tall
  // empty gap between them. Wrap the editor + its relocated toolbar in ONE
  // rounded, bordered surface so it reads like a single GMail-style composer.
  // All overrides are scoped under this class so the shared add/reply composers
  // are untouched.
  composer: {
    maxWidth: '680px',
    border: `1px solid ${theme.palette.type === 'dark' ? DARK_TEXT_BACKGROUND_COLOR : '#dadce0'}`,
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.palette.type === 'dark' ? DARK_TEXT_BACKGROUND_COLOR : 'white',
    // Drop Quill's default full box border on the editor - the surface above
    // now provides the single border.
    '& .ql-container.ql-snow': {
      border: 'none'
    },
    // Shrink the fixed 8rem empty editor box so a short comment no longer sits
    // in a big blank area; it still grows with content.
    '& [id^="editorBox-"]': {
      minHeight: '3.5rem !important'
    },
    // Relocated toolbar: a flush full-width bar inside the surface with a
    // hairline divider, instead of a detached rounded pill floating below.
    '& .ql-toolbar.ql-snow': {
      width: '100%',
      maxWidth: '100%',
      borderRadius: 0,
      margin: 0,
      borderTop: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.12)' : '#e8eaed'}`
    }
  },
  // Action row (Update / Cancel / "Storing locally") lives INSIDE the composer
  // surface, below the toolbar - like GMail's compose box where the Send button
  // and the formatting controls are all inside the box you type into
  // (T-all-2209, C-all-1011).
  composerFooter: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    padding: '6px 8px',
    backgroundColor: theme.palette.type === 'dark' ? DARK_TEXT_BACKGROUND_COLOR : 'white'
  },
  storageIndicator: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: 'bold',
    // The composer footer keeps its light surface in dark mode, so the theme's
    // white text is unreadable here (T-all-2254).
    color: theme.palette.type === 'dark' ? 'black' : undefined,
  },
  button: {
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 'bold',
    margin: 8,
    textTransform: 'none',
  },
  buttonPrimary: {
    backgroundColor: '#2d9cdb',
    color: '#fff',
    "&:hover": {
      backgroundColor: darken('#2d9cdb', 0.08)
    },
    "&:focus": {
      backgroundColor: darken('#2d9cdb', 0.24)
    },
  },
  todoLabelType: {
    alignSelf: "start",
    display: "inline-flex"
  },
  commentType: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    paddingBottom: '15px'
  },
  commentTypeGroup: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    }
  },
  chipItem: {
    color: '#fff',
    borderRadius: '8px',
    '& .MuiChip-label': {
      fontSize: 12,
    },
    '& .MuiFormControlLabel-label': {
      paddingRight: '5px',
      fontWeight: 'bold',
      textTransform: 'capitalize',
      [theme.breakpoints.down('sm')]: {
        height: '100%',
        verticalAlign: 'middle',
        display: 'inline-block',
        '& .MuiSvgIcon-root': {
          display: 'block'
        }
      },
    },
    '& .MuiChip-avatar': {
      width: '16px',
      height: '14px',
      color: '#fff',
    },
    '& .MuiRadio-colorPrimary.Mui-checked':{
      '&.Mui-checked': {
        color: 'white'
      }
    },
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    margin: theme.spacing(0, 0, 0, 4),
    [theme.breakpoints.down('sm')]: {
      margin: '10px'
    },
  },
  selected: {
    opacity: 1
  },
  unselected: {
    opacity: '.6'
  },
  chipItemQuestion: {
    background: '#2F80ED',
  },
  chipItemIssue: {
    background: '#E85757',
    color: 'black'
  },
  chipItemSuggestion: {
    background: '#e6e969',
    color: 'black'
  },
  chipItemTodo: {
    background: '#F29100',
    color: 'black'
  },
  chipItemFor: {
    background: '#73B76C',
  },
  chipItemAgainst: {
    background: '#D54F22',
  },
  chipItemReport: {
    background: '#73B76C',
  },
  commentTypeContainer: {
    borderRadius: '4px 4px 0 0'
  }
}), { name: 'CommentEdit' });

export function getIcon(commentType, color = undefined) {

  switch (commentType) {
    case SUGGEST_CHANGE_TYPE: {
      return <EmojiObjectsIcon />;
    }
    case ISSUE_TYPE: {
      return <BlockIcon />;
    }
    case QUESTION_TYPE: {
      return <QuestionIcon />;
    }
    case TODO_TYPE: {
      return <AssignmentIcon />;
    }
    case REPORT_TYPE: {
      if (color === 'BLUE') {
        return <Note />;
      }
      return <Feedback />;
    }
    default: {
      return null;
    }
  }
}

function CommentEdit(props) {
  const {
    marketId, onSave = () => {}, onCancel = () => {}, comment, myNotificationType, editState, updateEditState, editStateReset, 
    isWizard = false, messages, subscribed, isDisplayOfSubTask
  } = props;
  const {
    uploadedFiles,
    notificationType
  } = editState;
  const [groupPresencesState] = useContext(GroupMembersContext);
  const [marketsState] = useContext(MarketsContext);
  const [investiblesState] = useContext(InvestiblesContext);
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const submissionNavigation = useSubmissionNavigation();
  const editBox = useRef(null);
  const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const { id, uploaded_files: initialUploadedFiles, comment_type: commentType, investible_id: investibleId,
    body: initialBody, group_id: groupId, version, notification_type: commentNotificationType } = comment;
  const initiallySent = comment.is_sent !== false;
  const classes = useStyles();
  const wizardClasses = useContext(WizardStylesContext);
  const [, setOperationRunning] = useContext(OperationInProgressContext);
  const [commentState, commentDispatch] = useContext(CommentsContext);
  const [investibleState, investibleDispatch] = useContext(InvestiblesContext);
  const [messagesState, messagesDispatch] = useContext(NotificationsContext);
  const [marketPresencesState] = useContext(MarketPresencesContext);
  const presences = getMarketPresences(marketPresencesState, marketId);
  const myPresence = presences?.find((presence) => presence.current_user) || {};
  const [marketStagesState] = useContext(MarketStagesContext);
  const groupPresences = getGroupPresences(presences, groupPresencesState, marketId, groupId) || [];
  const investedOrAddressed = calculateInvestibleVoters(investibleId, marketId, marketsState, investiblesState,
    presences, true, true);
  const subscribedReal = subscribed ? subscribed : presences.filter((presence) =>{
    if (groupPresences.find((member) => member.id === presence.id)) {
      return true;
    }
    return investedOrAddressed.find((addressee) => addressee.id === presence.id);
  });
  const subscribedNotMe = subscribedReal.filter((presence) => presence.id !== myPresence?.id);
  const noSubscribedToSendTo = _.isEmpty(subscribedNotMe);
  const isTask = investibleId && commentType === TODO_TYPE;
  const isInvestibleNote = commentType === REPORT_TYPE && commentNotificationType === 'BLUE';

  const editorName = `comment-edit-editor${id}`;
  const editorSpec = {
    value: getQuillStoredState(editorName) || initialBody,
    onUpload: (files) => updateEditState({uploadedFiles: files}),
    mentionsAllowed: true,
    marketId,
    autoFocus: true
  }
  const [Editor] = useEditor(editorName, editorSpec);

  function handleSave(isSent) {
    const storedBody = getQuillStoredState(editorName);
    const body = storedBody !== null ? storedBody : initialBody;
    const currentUploadedFiles = uploadedFiles || [];
    const existingUploadedFiles = initialUploadedFiles || [];
    const newUploadedFiles = _.uniqBy([...existingUploadedFiles, ...currentUploadedFiles], 'path');
    const {
      uploadedFiles: filteredUploads,
      text: tokensRemoved,
    } = processTextAndFilesForSave(newUploadedFiles, body);
    if (tokensRemoved === initialBody && isSent === initiallySent) {
      sendIntlMessage(ERROR, 'errorCommentUpdateUnchanged');
      setOperationRunning(false);
      return Promise.resolve(false);
    }
    // Only images the body still references must be loaded - a path absent from the
    // body was removed by the user, not a load failure. Deriving removal from the
    // body instead of session state survives the remounts and refreshes that used
    // to wedge saves after a delete plus add (B-all-488)
    const requiredUploads = (initialUploadedFiles || []).filter((file) => body.includes(file.path));
    const imagesLoaded = allImagesLoaded(editBox?.current, requiredUploads);
    if (!imagesLoaded) {
      sendInfoPersistent({ id: 'loadImageError' }, {});
      return Promise.resolve(false);
    }
    const mentions = getMentionsFromText(tokensRemoved);
    const myActualNotificationType = commentType === TODO_TYPE && !investibleId ? myNotificationType :
      (commentType === REPORT_TYPE ? notificationType : undefined);
    return updateComment({marketId, commentId: id, body: tokensRemoved, uploadedFiles: filteredUploads, mentions,
      notificationType: myActualNotificationType, isSent, version})
      .then((response) => {
        let comment = response;
        resetEditor(editorName);
        onCommentOpen(investibleState, investibleId, marketStagesState, marketId, comment, investibleDispatch,
          commentState, commentDispatch, myPresence);
        deleteOrDehilightMessages(messages || [], messagesDispatch, true, true);
        if (commentType === REPORT_TYPE) {
          const message = findMessageOfType('REPORT_REQUIRED', investibleId, messagesState);
          if (message) {
            messagesDispatch(removeMessages([message.type_object_id]));
          }
        }
        setOperationRunning(false);
        handleSpinStop();
        const destination = submissionNavigation(response);
        if (destination) {
          navigate(history, destination.nextLink || destination.respondedLink);
        }
      })
  }

  function handleSpinStop() {
    editStateReset();
    onSave();
  }

  function handleCancel() {
    resetEditor(editorName);
    editStateReset();
    onCancel();
  }
  if (isWizard) {
    const isQuestion = commentType === QUESTION_TYPE;
    return (
      <WizardStepContainer
        {...props}
        isLarge
      >
        <Typography className={wizardClasses.introText}>
          What is your {isQuestion ? 'question' : 'blocking issue'}?
        </Typography>
        <Typography className={wizardClasses.introSubText} variant="subtitle1">
          Pick up where you left off with this {isQuestion ? 'question' : 'blocking issue'}.
        </Typography>
        {!noSubscribedToSendTo && !isTask && !isInvestibleNote && (
          <Typography className={classes.introSubText} variant="subtitle1">
            <GravatarGroup users={subscribedNotMe}/>
            notified unless use @ mentions.
          </Typography>
        )}
        {Editor}
        <div className={wizardClasses.borderBottom} />
        <WizardStepButtons
          {...props}
          nextLabel={isQuestion ? 'JobCommentAddQUESTION' : 'JobCommentAddISSUE'}
          isFinal={false}
          onNext={() => {
              if (getQuillStoredState(editorName) !== initialBody) {
                handleSave(false);
              }
            }
          }
          onTerminate={() => handleSave(true).then(() => 
            navigate(history, formCommentLink(comment.market_id, comment.group_id, comment.investible_id, comment.id)))}
          showTerminate
          terminateLabel="commentAddSendLabel"/>
      </WizardStepContainer>
    );
  }

  return (
    <>
      {!noSubscribedToSendTo && !isTask && !isDisplayOfSubTask && !isInvestibleNote && (
        <Typography className={classes.introSubText} variant="subtitle1">
          <GravatarGroup users={subscribedNotMe}/>
          notified unless use @ mentions.
        </Typography>
      )}
      <div className={classes.composer} ref={editBox}>
        {Editor}
        <div className={classes.composerFooter}>
          <SpinningIconLabelButton
            icon={Update}
            onClick={() => handleSave(true)}
            whiteBackground
            id="updateCommentButton"
          >
            {intl.formatMessage({ id: 'update' })}
          </SpinningIconLabelButton>
          <SpinningIconLabelButton onClick={handleCancel} doSpin={false} icon={Clear} whiteBackground>
            {intl.formatMessage({ id: 'cancel' })}
          </SpinningIconLabelButton>
          {!mobileLayout && (
            <Typography className={classes.storageIndicator}>
              {intl.formatMessage({ id: 'edited' })}
            </Typography>
          )}
        </div>
      </div>
      </>
  );
}

CommentEdit.propTypes = {
  marketId: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  comment: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  isWizard: PropTypes.bool
};

export default CommentEdit;
